import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import { LoadingSpinner } from '../components';
import { Package, MapPin, Heart, Bell, Lock, HelpCircle, Info, Edit, LogOut, User } from 'lucide-react';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, customer, logout, refreshProfile, updateCustomerData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Email OTP state
  const [emailStep, setEmailStep] = useState('idle'); // 'idle' | 'otp'
  const [emailOtp, setEmailOtp] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    if (customer) {
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email || '',
      });
    }
    refreshProfile();
  }, [isAuthenticated]);

  useEffect(() => {
    if (customer) {
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email || '',
      });
      setNewEmail(customer.email || '');
    }
  }, [customer]);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setIsSaving(true);
    try {
      const res = await apiService.updateProfile({
        first_name: formData.first_name,
        last_name: formData.last_name,
      });
      if (res?.success !== false) {
        updateCustomerData(formData);
        setIsEditing(false);
        setSuccess('Profile updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res?.error || 'Failed to update profile');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendEmailOTP = async () => {
    if (!newEmail || !newEmail.includes('@')) { setError('Enter a valid email'); return; }
    setEmailLoading(true);
    setError('');
    try {
      const res = await apiService.sendEmailOtp(newEmail);
      if (res?.success !== false) {
        setEmailStep('otp');
      } else {
        setError(res?.error || 'Failed to send OTP');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyEmailOTP = async () => {
    if (!emailOtp || emailOtp.length < 4) { setError('Enter the OTP'); return; }
    setEmailLoading(true);
    setError('');
    try {
      const res = await apiService.verifyEmailOtp(newEmail, emailOtp);
      if (res?.success !== false) {
        updateCustomerData({ email: newEmail });
        setEmailStep('idle');
        setEmailOtp('');
        setSuccess('Email updated!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res?.error || 'Invalid OTP');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/customer/login');
  };

  const menuItems = [
    { icon: <Package size={20} />, label: 'My Orders', path: '/customer/orders' },
    { icon: <MapPin size={20} />, label: 'Saved Addresses', path: '/customer/addresses' },
    { icon: <Heart size={20} />, label: 'Favorites', path: '/customer/favorites' },
    { icon: <Bell size={20} />, label: 'Notifications', path: '/customer/notification-settings' },
    { icon: <Lock size={20} />, label: 'Security', path: '/customer/security' },
    { icon: <HelpCircle size={20} />, label: 'Help & Support', path: '/customer/support' },
    { icon: <Info size={20} />, label: 'About', path: '/customer/about' },
  ];

  if (!customer) return <div className="page-container"><LoadingSpinner /></div>;

  return (
    <div className="profile-page">
      {/* Profile Header */}
      <div className="profile-hero">
        <div className="profile-avatar-large">
          {customer.profile_image_url ? (
            <img src={customer.profile_image_url} alt="Profile" className="profile-avatar-img" />
          ) : (
            <div className="profile-avatar-placeholder">
              <User size={64} strokeWidth={1.5} />
            </div>
          )}
        </div>
        <h2 className="profile-name">{customer.first_name} {customer.last_name}</h2>
        <p className="profile-phone">{customer.phone_number || customer.phone || ''}</p>
        {customer.email && <p className="profile-email">{customer.email}</p>}
      </div>

      {/* Edit Profile */}
      <div className="profile-section">
        {!isEditing ? (
          <button className="btn-outline btn-full" onClick={() => setIsEditing(true)}>
            <Edit size={18} /> Edit Profile
          </button>
        ) : (
          <div className="edit-form">
            <h3 className="section-title">Edit Profile</h3>
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.first_name}
                onChange={e => setFormData(d => ({ ...d, first_name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.last_name}
                onChange={e => setFormData(d => ({ ...d, last_name: e.target.value }))}
              />
            </div>

            {/* Email Update */}
            <div className="form-group">
              <label className="form-label">Email</label>
              {emailStep === 'idle' ? (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="email"
                    className="form-input"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                  {newEmail !== (customer.email || '') && (
                    <button className="btn-outline" onClick={handleSendEmailOTP} disabled={emailLoading}>
                      {emailLoading ? '...' : 'Verify'}
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <p>OTP sent to {newEmail}</p>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter OTP"
                    value={emailOtp}
                    onChange={e => setEmailOtp(e.target.value)}
                  />
                  <button className="btn-primary" onClick={handleVerifyEmailOTP} disabled={emailLoading}>
                    {emailLoading ? '...' : 'Verify OTP'}
                  </button>
                </div>
              )}
            </div>

            {error && <p className="auth-error">{error}</p>}
            {success && <p className="success-msg">{success}</p>}

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn-outline" onClick={() => { setIsEditing(false); setError(''); }}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>

      {success && !isEditing && <div className="success-toast">{success}</div>}

      {/* Menu Links */}
      <div className="profile-menu">
        {menuItems.map(item => (
          <button
            key={item.path}
            className="profile-menu-item"
            onClick={() => navigate(item.path)}
          >
            <span className="menu-icon">{item.icon}</span>
            <span className="menu-label">{item.label}</span>
            <span className="menu-arrow">›</span>
          </button>
        ))}
      </div>

      {/* Logout */}
      <div className="profile-section">
        <button className="btn-danger btn-full" onClick={handleLogout}>
          <LogOut size={18} /> Logout
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;
